import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Tooltip, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import SectionLabel from '../Common/SectionLabel';
import salesforceTasksService from 'services/salesforceTasksService';
import { useNotificationContext } from 'contexts/NotificationContext';
import FormField from '../Common/FormField';
import SalesforceTaskDueDatePicker from './SalesforceTaskDueDatePicker';
import {
	isSalesforceTaskCommentsTooLong,
	validateSalesforceTaskDueDate,
} from './salesforceTaskValidation';
import SalesforceTaskCommentsField from './SalesforceTaskCommentsField';
import SalesforceTaskCreateFormSkeleton from './SalesforceTaskCreateFormSkeleton';
import BreadcrumbBackButton from '../Common/BreadcrumbBackButton';

const CONVERT_TO_INTERACTION_OPTIONS = ['No', 'Yes'];
const CONVERT_TO_INTERACTION_HELP_TEXT = 'On completion of this Task, should it be converted into an Interaction record to be logged as activity with the related record(s)?';

const fieldStyles = {
	'& .MuiInputBase-root': {
		fontSize: '12px',
		borderRadius: '6px',
		backgroundColor: 'background.paper',
	},
	'& .MuiInputBase-input': {
		fontSize: '12px',
	},
	'& .MuiFormHelperText-root': {
		fontSize: '10px',
		marginLeft: 0,
	},
};

// Creates a CRM task related either to an alert's Salesforce record (`alert`)
// or directly to a Salesforce record (`relatedRecord`, e.g. a Fundraising).
// The subject defaults to the alert title, or to the related record's name
// when there is no alert. The alert-only extras (AI subject suggestion,
// breadcrumb and defaults banner) are hidden when there is no alert.
const SalesforceTaskCreateForm = ({
	alert,
	relatedRecord,
	formOptions,
	formOptionsLoading,
	onBack,
	onCancel,
	onCreateTaskClick,
	onCreated,
	onFooterActionsChange,
}) => {
	const { showNotification } = useNotificationContext();

	const [formValues, setFormValues] = useState({
		subject: alert
			? (alert.event?.event_title ?? '')
			: (relatedRecord?.name ?? ''),
		owner_id: '',
		status: '',
		priority: '',
		comments: '',
		convert_to_interaction: 'No',
	});

	const [dueDate, setDueDate] = useState(dayjs().startOf('day'));
	const [hasChangedDueDate, setHasChangedDueDate] = useState(false);

	const [assignableUsers, setAssignableUsers] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const [priorities, setPriorities] = useState([]);

	const [errors, setErrors] = useState({});
	const [loadingMetadata, setLoadingMetadata] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const [generatingSubject, setGeneratingSubject] = useState(false);
	const [hasGeneratedSubject, setHasGeneratedSubject] = useState(false);

	const relatedRecordName =
		alert?.salesforce_object?.record_name ?? relatedRecord?.name ?? null;

	const dueDateValidation = validateSalesforceTaskDueDate(dueDate);
	const isDueDateInvalid = !dueDateValidation.isValid;

	const isCommentsTooLong = isSalesforceTaskCommentsTooLong(
		formValues.comments
	);

	// Apply the available task options and derive the initial form defaults
	// from the current user, available statuses, and alert severity (Salesforce's
	// standard 'Normal' priority when there is no alert)
	const applyFormOptions = (options) => {
		const users = options?.assignable_users ?? [];
		const taskStatuses = options?.statuses ?? [];
		const taskPriorities = options?.priorities ?? [];
		const severityPriorities = options?.severity_priorities ?? {};

		setAssignableUsers(users);
		setStatuses(taskStatuses);
		setPriorities(taskPriorities);

		const currentUser = users.find((user) => user.is_current_user);

		const defaultPriority = alert
			? (severityPriorities[alert.severity?.toLowerCase()] ?? '')
			: 'Normal';

		setFormValues((previous) => ({
			...previous,
			owner_id: currentUser?.sf_user_id_in_client_org ?? '',
			status: taskStatuses.includes('Not Started')
				? 'Not Started'
				: (taskStatuses[0] ?? ''),
			priority: taskPriorities.includes(defaultPriority)
				? defaultPriority
				: (taskPriorities[0] ?? ''),
		}));
	};

	// Generate a task-focused subject from the alert,
	// preventing duplicate requests and allowing the user to retry if generation fails
	const handleSuggestSubject = async () => {
		if (generatingSubject || hasGeneratedSubject) {
			return;
		}

		try {
			setGeneratingSubject(true);

			const response = await salesforceTasksService.suggestSubject(alert.id);

			if (!response?.subject) {
				throw new Error('Unable to generate task subject');
			}

			setFormValues((previous) => ({
				...previous,
				subject: response.subject,
			}));

			setErrors((previous) => ({
				...previous,
				subject: undefined,
			}));

			setHasGeneratedSubject(true);
		} catch (error) {
			console.error('Failed to generate Salesforce task subject:', error);

			showNotification(
				error.message || 'Unable to generate task subject',
				'error'
			);
		} finally {
			setGeneratingSubject(false);
		}
	};

	const handleDueDateChange = (value) => {
		setHasChangedDueDate(true);
		setDueDate(value);

		if (errors.due_date) {
			setErrors((previous) => ({
				...previous,
				due_date: undefined,
			}));
		}
	};

	const handleChange = (field) => (event) => {
		const value = event.target.value;

		setFormValues((previous) => ({
			...previous,
			[field]: value,
		}));

		if (errors[field]) {
			setErrors((previous) => ({
				...previous,
				[field]: undefined,
			}));
		}
	};

	const validate = useCallback(() => {
		const nextErrors = {};

		if (!formValues.subject.trim()) {
			nextErrors.subject = 'Subject is required';
		}

		if (!formValues.owner_id) {
			nextErrors.owner_id = 'Assigned To is required';
		}

		if (!formValues.status) {
			nextErrors.status = 'Status is required';
		}

		if (!formValues.priority) {
			nextErrors.priority = 'Priority is required';
		}

		if (isDueDateInvalid) {
			nextErrors.due_date = dueDateValidation.errorMessage;
		}

		if (isCommentsTooLong) {
			nextErrors.comments = 'Comments exceed the maximum allowed length';
		}

		setErrors(nextErrors);

		return Object.keys(nextErrors).length === 0;
	}, [formValues, errors, isDueDateInvalid, isCommentsTooLong]);

	// Validate and create the CRM task, preventing duplicate submissions and
	// notifying the parent after a successful creation so related data can refresh
	const handleSubmit = useCallback(
		async (event) => {
			onCreateTaskClick?.();
			event.preventDefault();

			if (submitting || !validate()) {
				return;
			}

			try {
				setSubmitting(true);

				const response = await salesforceTasksService.createTask({
					...(alert
						? { input_signal_id: alert.id }
						: { what_id: relatedRecord?.id }),
					subject: formValues.subject.trim(),
					owner_id: formValues.owner_id,
					status: formValues.status,
					priority: formValues.priority,
					due_date: dueDateValidation.formattedValue,
					comments: formValues.comments.trim(),
					convert_to_interaction: formValues.convert_to_interaction,
				});

				showNotification('CRM task created successfully', 'success');

				await onCreated?.(response);
			} catch (error) {
				console.error('Failed to create Salesforce task:', error);

				showNotification(
					error.message || 'Unable to create Salesforce task.',
					'error'
				);
			} finally {
				setSubmitting(false);
			}
		},
		[
			submitting,
			validate,
			alert,
			relatedRecord,
			formValues,
			dueDateValidation.formattedValue,
			onCreated,
		]
	);

	// Initialize the form when the preloaded task options become available
	useEffect(() => {
		if (formOptionsLoading || !formOptions) {
			setLoadingMetadata(true);
			return;
		}

		applyFormOptions(formOptions);
		setLoadingMetadata(false);
	}, [formOptions, formOptionsLoading]);

	// Publishes the form footer buttons (Cancel / Create Task) up to AlertsDrawer,
	// This lets the Drawer own all footer rendering/styling.
	// Dependencies are memoized using useCallback (along with their own dependencies)
	// so they stay stable across renders.
	// Without that, this effect would re-run on every render and cause an infinite
	// loop with AlertsDrawer.
	useEffect(() => {
		onFooterActionsChange?.([
			{
				key: 'cancel',
				label: 'Cancel',
				onClick: onCancel,
				disabled: submitting,
				variant: 'secondary',
			},
			{
				key: 'submit',
				label: 'Create Task',
				onClick: handleSubmit,
				disabled: submitting,
				variant: 'primary',
			},
		]);
	}, [
		submitting,
		handleSubmit,
		onCancel,
		onFooterActionsChange,
		validate,
		isCommentsTooLong,
	]);

	if (loadingMetadata) {
		return <SalesforceTaskCreateFormSkeleton />;
	}

	return (
		<Box
			component='form'
			onSubmit={handleSubmit}
			noValidate
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				minHeight: 0,
			}}
		>
			{/* Form content */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					padding: '16px 18px',
				}}
			>
				{/* Breadcrumb */}
				{onBack && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							mb: '12px',
							minWidth: 0,
						}}
					>
						<BreadcrumbBackButton
							label={alert?.event?.event_title || 'Alert'}
							onClick={onBack}
						/>

						<Typography
							variant='body2'
							sx={{
								color: 'text.tertiary',
								flexShrink: 0,
							}}
						>
							/
						</Typography>

						<Typography
							variant='body2'
							sx={{
								color: 'text.secondary',
								flexShrink: 0,
								whiteSpace: 'nowrap',
							}}
						>
							Create Task
						</Typography>
					</Box>
				)}

				{/* Info banner */}
				{alert && (
					<Box
						sx={{
							mb: '16px',
							padding: '10px 12px',
							borderRadius: '6px',
							backgroundColor: 'status.blue.bg',
							border: '1px solid',
							borderColor: 'status.blue.border',
						}}
					>
						<Typography
							variant='body2'
							sx={{
								color: 'status.blue.text',
							}}
						>
							These values have been defaulted based on this alert and can be
							adjusted before saving.
						</Typography>
					</Box>
				)}

				<SectionLabel text='Task Details' />

				<Box
					sx={{
						backgroundColor: 'background.subtle',
						borderRadius: '8px',
						padding: '8px 10px',
					}}
				>
					<FormField label='Subject' required>
						<Box>
							<TextField
								fullWidth
								size='small'
								value={formValues.subject}
								onChange={handleChange('subject')}
								error={!!errors.subject}
								helperText={errors.subject}
								disabled={generatingSubject}
								inputProps={{
									'data-testid': 'task-subject-input',
								}}
								sx={fieldStyles}
							/>

							{/* The suggestion is generated from the alert */}
							{alert && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'flex-end',
										mt: '4px',
									}}
								>
									<Tooltip
										title='Generates a task-focused subject based on this alert'
										placement='top'
										arrow
									>
										<span>
											<LoadingButton
												type='button'
												variant='text'
												size='small'
												onClick={handleSuggestSubject}
												loading={generatingSubject}
												disabled={hasGeneratedSubject}
												data-testid='suggest-task-subject-button'
												sx={{
													minWidth: 0,
													padding: '2px 4px',
													fontSize: '11px',
													fontWeight: 500,
													color: 'primary.main',
												}}
											>
												{hasGeneratedSubject
													? 'Suggestion applied'
													: 'Suggest with AI'}
											</LoadingButton>
										</span>
									</Tooltip>
								</Box>
							)}
						</Box>
					</FormField>

					<FormField label='Assigned To' required>
						<TextField
							select
							fullWidth
							size='small'
							value={formValues.owner_id}
							onChange={handleChange('owner_id')}
							error={!!errors.owner_id}
							helperText={errors.owner_id}
							inputProps={{
								'data-testid': 'task-assigned-to-select',
							}}
							sx={fieldStyles}
						>
							{assignableUsers.map((user) => (
								<MenuItem
									key={user.sf_user_id_in_client_org}
									value={user.sf_user_id_in_client_org}
								>
									{user.name}
								</MenuItem>
							))}
						</TextField>
					</FormField>

					<FormField label='Status' required>
						<TextField
							select
							fullWidth
							size='small'
							value={formValues.status}
							onChange={handleChange('status')}
							error={!!errors.status}
							helperText={errors.status}
							inputProps={{
								'data-testid': 'task-status-select',
							}}
							sx={fieldStyles}
						>
							{statuses.map((status) => (
								<MenuItem key={status} value={status}>
									{status}
								</MenuItem>
							))}
						</TextField>
					</FormField>

					<FormField label='Priority' required>
						<TextField
							select
							fullWidth
							size='small'
							value={formValues.priority}
							onChange={handleChange('priority')}
							error={!!errors.priority}
							helperText={errors.priority}
							inputProps={{
								'data-testid': 'task-priority-select',
							}}
							sx={fieldStyles}
						>
							{priorities.map((priority) => (
								<MenuItem key={priority} value={priority}>
									{priority}
								</MenuItem>
							))}
						</TextField>
					</FormField>

					<FormField label='Due Date' required>
						<SalesforceTaskDueDatePicker
							value={dueDate}
							onChange={handleDueDateChange}
							validation={dueDateValidation}
							disabled={submitting}
							showError={hasChangedDueDate || !!errors.due_date}
						/>
					</FormField>

          <FormField
						label='Convert to Interaction?'
						helperText={CONVERT_TO_INTERACTION_HELP_TEXT}
					>
						<TextField
							select
							fullWidth
							size='small'
							value={formValues.convert_to_interaction}
							onChange={handleChange('convert_to_interaction')}
							inputProps={{
								'data-testid': 'convert-to-interaction-select',
							}}
							sx={fieldStyles}
						>
							{CONVERT_TO_INTERACTION_OPTIONS.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
					</FormField>

					<FormField label='Related To'>
						<Box
							data-testid='task-related-to'
							sx={{
								minHeight: 40,
								display: 'flex',
								alignItems: 'center',
								padding: '8px 12px',
								border: '1px solid',
								borderColor: 'border.light',
								borderRadius: '6px',
								backgroundColor: 'background.subtle',
							}}
						>
							<Typography
								sx={{
									fontSize: '12px',
									color: relatedRecordName ? 'text.primary' : 'text.secondary',
								}}
							>
								{relatedRecordName || '—'}
							</Typography>
						</Box>
					</FormField>

					<FormField label='Comments'>
						<SalesforceTaskCommentsField
							value={formValues.comments}
							onChange={handleChange('comments')}
							disabled={submitting}
						/>
					</FormField>


				</Box>
			</Box>

			{submitting && (
				<Box
					data-testid='task-create-loading-overlay'
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255, 255, 255, 0.6)',
						zIndex: 1,
					}}
				>
					<CircularProgress size={28} />
				</Box>
			)}
		</Box>
	);
};

SalesforceTaskCreateForm.propTypes = {
	alert: PropTypes.object,
	relatedRecord: PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string,
	}),
	formOptions: PropTypes.object,
	formOptionsLoading: PropTypes.bool,
	onBack: PropTypes.func,
	onCancel: PropTypes.func.isRequired,
	onCreateTaskClick: PropTypes.func,
	onCreated: PropTypes.func.isRequired,
	onFooterActionsChange: PropTypes.func.isRequired,
};

export default SalesforceTaskCreateForm;
